import React, { useContext } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Link } from "gatsby";
import Container from "@src/components/Container";
import { Box, Flex } from "@src/components/Boxes";
import Translate from "@src/components/translation/Translate";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { AppDataContext } from "@src/components/AppDataContext";
import { getRelativePath } from "@src/utils/routes";
import { getWelcomeBackUrl } from "@src/utils/getWelcomeBackUrl";
import { incrementMetric } from "@src/utils/logClient";
import { Header as MDSHeader, Button } from "@web-monorepo/mds";

const bottomBorder = css`
  border-bottom: 1px solid #eee;
`;

const Nav = styled.div`
  position: fixed;
  background-color: white;
  right: 0;
  left: 0;
  z-index: 1030;
  flex: 0 0 auto;
  top: 0;
`;

const Logo = styled.img`
  width: 154px;
  min-width: 154px;
`;

const LoggedInText = styled.span`
  color: var(--dt-color-contentAccent);
  font-weight: 600;
  line-height: 22px;
  font-family: "Ginto", "Helvetica Neue", Helvetica, Arial, sans-serif;
  word-break: keep-all;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--dt-color-contentAccentHover);
    text-decoration: none;
  }
`;

const LinkText = styled.span`
  color: var(--dt-color-dt_taro90);
  font-weight: 500;
  line-height: 22px;
  font-family: "Ginto", "Helvetica Neue", Helvetica, Arial, sans-serif;
  word-break: keep-all;
  white-space: nowrap;
  padding-inline: 5px;

  &:hover {
    color: var(--dt-color-contentAccentHover);
    text-decoration: none;
  }
`;

const IdeasHeader: React.FC = () => {
  const { data: appData } = useContext(AppDataContext);
  const modalContext = useContext(ModalContext);

  function openLoginModal() {
    modalContext.showModal(ModalType.TeacherLogin, { form: { noRedirect: true } });
  }

  function openSignupModal() {
    modalContext.showModal(ModalType.TeacherSignup);
  }

  function openWelcomeBackLink() {
    incrementMetric("external_site.welcome_back_click");
    // @ts-expect-error Can be `undefined`
    const welcomeBackUrl = getWelcomeBackUrl(appData.type, appData.userData?.isChildAsParent);

    if (typeof window !== "undefined") {
      window.location.href = welcomeBackUrl;
    }
  }

  // Determine what to render for login/auth section
  let loginPart: React.ReactNode = null;

  if (appData.loadingSession) {
    // Session is loading - show basic login links
    loginPart = (
      <>
        <a href="https://teach.classdojo.com/">
          <LinkText>
            <Translate path="layouts.main.teacher_login" />
          </LinkText>
        </a>
        <a href="https://home.classdojo.com/">
          <LinkText>
            <Translate path="layouts.main.parent_login" />
          </LinkText>
        </a>
      </>
    );
  } else if (appData.userData) {
    // User is logged in - show welcome back message
    loginPart = (
      <LoggedInText onClick={openWelcomeBackLink}>
        {appData.name ? (
          <Translate path="components.welcome_banner.hi" subs={{ name: appData.name }} />
        ) : (
          <Translate path="components.welcome_banner.welcome_back_anon" />
        )}
        <span>! </span>
        <Translate path="components.welcome_banner.come_in" />
      </LoggedInText>
    );
  } else {
    // User is not logged in - show login/signup buttons
    loginPart = (
      <>
        <Button onClick={openLoginModal} data-test-name="open-login-modal" size="s" variant="secondary">
          <Translate path="pages.home.log_in" />
        </Button>
        <Button size="s" variant="primary" onClick={openSignupModal} data-test-name="open-signup-modal">
          <Translate path="pages.home.sign_up" />
        </Button>
      </>
    );
  }

  return (
    <MDSHeader>
      <Nav role="navigation">
        <Box height="64px" css={bottomBorder}>
          <Container height="100%">
            <Flex flexDirection="row" alignItems="center" justifyContent="space-between" height="100%">
              {/* Logo */}
              <Link to={getRelativePath("/")}>
                <Logo src="https://new-external.classdojo.com/classdojo-logo.png" alt="ClassDojo" />
              </Link>

              {/* Desktop: Login/Signup buttons */}
              <Flex alignItems="center" display={["none", "none", "flex"]} css={{ gap: "16px" }}>
                {loginPart}
              </Flex>

              {/* Mobile: Menu Toggle */}
              <Box display={["block", "block", "none"]}>
                <MDSHeader.MobileMenuToggle />
              </Box>
            </Flex>
          </Container>
        </Box>
      </Nav>

      {/* Mobile Menu */}
      <MDSHeader.MobileMenu>
        <MDSHeader.MobileMenuFooter>
          <MDSHeader.Actions className="flex flex-col gap-y-16">
            <div
              css={{
                display: "flex",
                alignItems: "center",
                listStyle: "none",
                margin: 0,
                gap: 16,
                padding: 0,
                width: "100%",
              }}
            >
              {loginPart}
            </div>
          </MDSHeader.Actions>
        </MDSHeader.MobileMenuFooter>
      </MDSHeader.MobileMenu>
    </MDSHeader>
  );
};

export default IdeasHeader;
